import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Card, DatePicker, Icon, IndexTable, InlineStack, Link, Popover, Select, Text, TextField } from '@shopify/polaris';
import { TypedTripManifest, useGetTrip_manifests } from 'queries/trip_manifest.query';
import helpers from 'helpers/index';
import Pagination from 'components/pagination';
import dateandtime from 'date-and-time';
import { TypedJourney, useGetJourneys } from 'queries/journey.query';
import { TypedVehicle, useGetVehicles } from 'queries/vehicle.query';
import { CalendarIcon, EditIcon, AppExtensionIcon, FilterIcon } from '@shopify/polaris-icons';
import { TypedSeating, useGetSeatings } from 'queries/seating.query';
import type { IndexTableProps } from '@shopify/polaris';
import { useNotification } from 'context/NotificationContext';
import { ENUM_INVOICE_STATUS, useMutateExportInvoices, useMutatePreviewInvoices } from 'queries/invoices.query';
import AccountingInvoicePreviewModal, { PreviewInvoiceData } from 'entities/ticket-managers/components/AccountingInvoicePreviewModal';
import __ from 'languages/index';
import EditOrderTaxModal from 'entities/ticket-managers/components/editOrderTax.modal';
import { TypedOrders } from 'queries/orders.query';

export default function InvoiceManagers() {
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
      if (matches) {
        setShowFilters(false);
      }
    };

    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
    } else {
      mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
      } else {
        mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
      }
    };
  }, []);

  const rootSearchPath = window.location.search;
  const { addNotification } = useNotification();
  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  const StringQuery: any = helpers.ExtractUrl(rootSearchPath) || false;
  const initialDepartureTimestamp = String(StringQuery?.departure_date || '').match(/gte:(\d+)/)?.[1];
  const initialSelectedDate = initialDepartureTimestamp ? new Date(Number(initialDepartureTimestamp)) : new Date();
  const initialSelectedDateRange = helpers.getDayRange(initialSelectedDate);
  const initialDepartureDate = `gte:${initialSelectedDateRange.start},lte:${initialSelectedDateRange.end}`;
  const initialQuery = {
    query: '',
    page: 1,
    limit: 200,
    sort: 'departure_date:asc',
    'trip.journey_id': '',
    vehicle_id: '',
    seating_id: '' /** Sơ đồ ghế */,
    departure_date: initialDepartureDate,
    is_exported_invoice: '',
    total_occupied_seats: '!0',
  };

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });
  const { refetch: getEntities, data, isFetching: loading } = useGetTrip_manifests(mainQuery);

  const [entities, setEntities] = useState<TypedTripManifest[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [sum, setSum] = useState({ countTrip: 0, countVehicle: 0, totalPassenger: 0, totalTicket: 0, totalMoney: 0 });
  useEffect(() => {
    if (data) {
      let { records, totalItems, sum } = data;
      setEntities(records);
      setTotalItems(totalItems || 0);
      if (sum) setSum(sum);
    }
  }, [data]);
  /**
   * Change page number
   * Must be mainQuery or it will reset mainQuery. BUG!
   */
  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'Dashboard', '/' + buildURLSearch);
    }
  }, [mainQuery]);

  const { data: journeyFetchedData } = useGetJourneys({
    limit: 100,
    journey_parent: BigInt('0'),
    sort: 'createdAt:asc',
  });

  const journeyOptions = useMemo(() => {
    const journeys = journeyFetchedData?.body || [];
    return [
      { label: 'Tất cả', value: '' },
      ...journeys.map((journey: TypedJourney) => ({
        label: `${journey.journey_from || ''} - ${journey.journey_to || ''}`.trim(),
        value: String(journey.id),
      })),
    ];
  }, [journeyFetchedData]);

  const { data: vehicleFetchedData } = useGetVehicles({
    limit: 200,
    sort: 'createdAt:asc',
  });

  const licenseOptions = useMemo(() => {
    const vehicles = vehicleFetchedData?.body || [];
    return [
      { label: 'Tất cả', value: '' },
      ...vehicles.map((vehicle: TypedVehicle) => ({
        label: vehicle.license_plate || vehicle.name || '--',
        value: String(vehicle.id),
      })),
    ];
  }, [vehicleFetchedData]);

  const { data: seatingFetchedData } = useGetSeatings({
    limit: 200,
    sort: 'createdAt:asc',
  });

  const seatingOptions = useMemo(() => {
    const seating = seatingFetchedData?.body || [];
    return [
      { label: 'Tất cả', value: '' },
      ...seating.map((seating: TypedSeating) => ({
        label: seating.seating_name ?? '--',
        value: String(seating.id),
      })),
    ];
  }, [seatingFetchedData]);

  /** Date filter */
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [{ month, year }, setDate] = useState({
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  });

  function handleMonthChange(month, year) {
    setDate({ month: month, year });
  }
  function handleDateSelection({ end: newSelectedDate }) {
    setSelectedDate(newSelectedDate);
    setVisible(false);
  }
  useEffect(() => {
    if (selectedDate) {
      setDate({
        month: selectedDate.getMonth(),
        year: selectedDate.getFullYear(),
      });
      let { start, end } = helpers.getDayRange(selectedDate);
      const departureDate = `gte:${start},lte:${end}`;
      setMainQuery((prev) => {
        if (prev.departure_date === departureDate && Number(prev.page) === 1) {
          return prev;
        }

        return {
          ...prev,
          departure_date: departureDate, // `gt:${selectedDate.getTime()}`,
          page: 1,
        };
      });
    }
  }, [selectedDate]);

  const columnHeadings: IndexTableProps['headings'] = [
    { title: 'Chuyến', id: 'column-header--journey' },
    { title: 'Khởi hành', id: 'column-header--departure-date' },
    { title: 'Loại hình', id: 'column-header--seating' },
    { title: 'Biển số', id: 'column-header--license_plate' },
    { title: 'Số ghế', id: 'column-header--total_seat' },
    { title: 'Tên trên hóa đơn', id: 'column-header--name' },
    { title: 'Số điện thoại', id: 'column-header--phone' },
    { title: 'Email nhận HD', id: 'column-header--email' },
    { title: 'Phương thức TT', id: 'column-header--payment-method' },
    { title: 'Tổng tiền', id: 'column-header--total' },
    { title: 'Loại hóa đơn', id: 'column-header--invoice-type' },
    { title: 'Hóa đơn', id: 'column-header--invoice-status' },
    { title: 'Tiền HĐ', id: 'column-header--invoice-amount' },
    { title: 'Công cụ', alignment: 'end', id: 'column-header--tools' },
  ];

  const resourceName = {
    singular: 'trip',
    plural: 'trips',
  };

  const getTripSelectionId = useCallback((manifest: TypedTripManifest, index: number) => {
    return String(manifest.id ?? `manifest-${index}`);
  }, []);

  // const selectableTripIds = useMemo(() => {
  //   if (helpers.isEmpty(entities)) return [];
  //   return entities?.reduce<string[]>((acc, manifest, index) => {
  //     if (manifest?.is_exported_invoice !== 1) {
  //       acc.push(getTripSelectionId(manifest, index));
  //     }
  //     return acc;
  //   }, []);
  // }, [entities, getTripSelectionId]);

  const [showEditTaxModal, setShowEditTaxModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewInvoiceData[] | null>(null);
  const [previewRequest, setPreviewRequest] = useState<{ manifest: string; mode: string; order: string } | null>(null);
  const [previewOrderId, setPreviewOrderId] = useState<string | null>(null);
  const [exportingOrderId, setExportingOrderId] = useState<string | null>(null);

  const { mutateAsync: getPreviewData, isPending: loadingPreview } = useMutatePreviewInvoices();
  const { mutateAsync: exportInvoiceMutation, isPending: exportingInvoice } = useMutateExportInvoices();

  const getPreviewDataCallback = useCallback(
    async (objectPreview: { manifest: any; mode: string; order: string }, orderId?: string) => {
      setPreviewOrderId(orderId);
      try {
        const response = await getPreviewData(objectPreview.manifest);
        setPreviewRequest(objectPreview);
        setPreviewData(response);
        setPreviewModalOpen(true);
      } catch (e: any) {
        addNotification('error', e?.message ?? 'Không thể xem trước hóa đơn!');
      } finally {
        setPreviewOrderId(null);
      }
    },
    [getPreviewData]
  );

  const handleExportInvoice = useCallback(
    async (payload: { manifest: any; mode: string; order: string }, orderId?: string) => {
      const targetOrderId = orderId || payload.order;
      setExportingOrderId(targetOrderId);
      try {
        await exportInvoiceMutation(payload.manifest);
        addNotification('info', 'Tạo hóa đơn thành công!');
        setPreviewModalOpen(false);
        getEntities();
      } catch (e) {
        addNotification('error', 'Không thể tạo hóa đơn!');
      } finally {
        setExportingOrderId(null);
      }
    },
    [addNotification, exportInvoiceMutation, getEntities]
  );

  /** Hàm chung nhấc ra */

  const handleEditTax = (orderId) => {
    if (!orderId) return;
    setCurrentOrderId(String(orderId));
    setShowEditTaxModal(true);
  };

  // mode = each_order // all // contract
  const buildInvoicePayload = (mode = 'all', manifest_id: any, order_id?: any) => ({
    manifest: String(manifest_id),
    mode: mode,
    order: order_id ? String(order_id) : undefined,
  });

  let rowPosition = 0;
  const rowMarkup = entities?.map((manifest, manifestIndex) => {
    const journey = manifest.trip?.journey;
    const tripTime = manifest.departure_hour || '--';
    const journeyName = journey?.journey_name || `${journey?.journey_from || ''} - ${journey?.journey_to || ''}`.trim() || '--';
    const departureDate = manifest.departure_date || manifest.trip?.departure_date;
    const formattedDepartureDate = departureDate ? dateandtime.format(new Date(Number(departureDate)), 'DD/MM/YYYY') : '--';
    const isExported = manifest?.is_exported_invoice === 1;
    const parentRowId = `manifest-row-${getTripSelectionId(manifest, manifestIndex)}`;
    const parentPosition = rowPosition;
    rowPosition += 1;

    const datedy = dateandtime.format(new Date(Number(manifest?.departure_date)), 'YYYY-MM-DD');
    const ticket_managers_link = `/ticket_managers?d=${datedy}&journey_id=${manifest?.trip?.journey_id}&htext=${manifest?.departure_hour}&h=${manifest?.trip?.id}`;

    // console.error(manifest, 'manifest');

    const hasInvoiceForALl = manifest.trip_manifest_invoice?.find((a) => a?.invoices?.exportMode === 'contract'); // exportMode
    // const hasOneInvoiceInOrder = manifest.trip_manifest_invoice?.find((a) => a.exportMode === 'each_order'); // exportMode
    // const allOtherInvoices = manifest.trip_manifest_invoice.filter((a) => {
    //   if (!hasInvoiceForALl) return true;
    //   return a.id !== hasInvoiceForALl.id;
    // });

    return (
      <Fragment key={parentRowId}>
        <IndexTable.Row
          rowType="data"
          id={parentRowId}
          position={parentPosition}
          selected={false}
          disabled={isExported || manifest?.total_occupied_seats < 1}
        >
          <IndexTable.Cell scope="col" id={parentRowId}>
            <Link removeUnderline url={ticket_managers_link}>
              <Text as="span" fontWeight="bold">
                <Badge tone="success">{tripTime}</Badge> • {journeyName}
              </Text>
            </Link>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {formattedDepartureDate}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {manifest.seating?.seating_name || '--'}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {manifest.vehicle?.license_plate || '--'}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>{`${manifest?.total_occupied_seats}`}</IndexTable.Cell>
          <IndexTable.Cell></IndexTable.Cell>
          <IndexTable.Cell></IndexTable.Cell>
          <IndexTable.Cell />
          <IndexTable.Cell />
          <IndexTable.Cell>
            <Badge tone="critical-strong">{helpers.formatNumberWithCommas(manifest.total_fare)}</Badge>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Badge size="small" tone={'info'}>
              {/* {hasInvoiceForALl ? __('tax_type_' + hasInvoiceForALl?.TaxType) : null} */}
            </Badge>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {hasInvoiceForALl ? (
              <Link url={process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${hasInvoiceForALl?.invoices.maTraCuu}` || '#'} target="_blank">
                {hasInvoiceForALl?.invoices.shDon}
              </Link>
            ) : null}
          </IndexTable.Cell>

          <IndexTable.Cell>
            {hasInvoiceForALl ? (
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(hasInvoiceForALl?.invoices.TgTTTBSo)}
              </Text>
            ) : null}
          </IndexTable.Cell>

          <IndexTable.Cell>
            <InlineStack align="end" blockAlign="center" gap="200" wrap={false}>
              {hasInvoiceForALl ? (
                <Text as="span" alignment="end" fontWeight="regular">
                  {isExported ? 'Đã phát hành' : 'Chưa phát hành'}
                </Text>
              ) : null}
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
        {(() => {
          const orderMap = new Map();

          (manifest.trip_manifest_ticket || []).forEach((ticket, ticketIndex) => {
            const orders = ticket.ticket?.orders;
            const orderId = orders?.id ?? `ticket-${ticket.ticket_id || ticket.id || ticketIndex}`;

            if (!orderMap.has(orderId)) {
              orderMap.set(orderId, { order: orders, tickets: [] });
            }

            orderMap.get(orderId).tickets.push(ticket);
          });

          return Array.from(orderMap.entries()).map(([orderId, orderGroup]) => {
            const _orders: TypedOrders = orderGroup.order;
            const customer = _orders?.customer_data;
            const order_invoice_info = _orders?.order_invoice_info;
            const customerName = customer?.display_name || _orders?.customer_name || 'Khách hàng';
            const tax_type = order_invoice_info?.tax_type || 'DEFAULT';
            const invoice_name = order_invoice_info?.name || 'Khách lẻ';
            const invoice_phone = order_invoice_info?.invoice_phone || '';
            const invoice_email = order_invoice_info?.invoice_email || '';
            const totalPriceValue = orderGroup.tickets.reduce((sum, ticket) => sum + Number(ticket.ticket?.price || 0), 0);
            const totalPrice = helpers.formatNumberWithCommas(totalPriceValue);
            const childRowId = `order-${orderId}`;
            const childPosition = rowPosition;
            rowPosition += 1;

            const isTicketExported = _orders?.is_exported_invoice === 1;

            const mainInvoice = _orders?.order_to_invoice?.find((a) => a.invoices.invoice_status === ENUM_INVOICE_STATUS.NORMAL);

            const isSigned = mainInvoice?.invoices?.is_signed || 0;

            const isPreviewLoading = loadingPreview && previewOrderId === String(orderId);
            const isExportLoading = exportingInvoice && exportingOrderId === String(orderId);

            let orderInvoice = null;

            if (hasInvoiceForALl) {
              if (mainInvoice?.invoices.invoice_id !== hasInvoiceForALl.invoice_id) {
                orderInvoice = _orders?.order_to_invoice;
              }
            } else {
              orderInvoice = _orders?.order_to_invoice;
            }

            return (
              <IndexTable.Row
                hideSelectable
                rowType="child"
                key={childRowId}
                id={childRowId}
                position={childPosition}
                disabled={_orders?.is_exported_invoice === 1}
              >
                <IndexTable.Cell scope="row" headers={`${columnHeadings[0]?.id} ${parentRowId}`}>
                  <Link removeUnderline url={`${ticket_managers_link}#${orderId}`}>
                    <Text as="span" fontWeight="bold">
                      {customerName}
                    </Text>
                  </Link>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {isTicketExported && orderInvoice ? (
                    <Text as="span" alignment="end" numeric>
                      {helpers.formatNumberWithCommas(orderInvoice?.trip_manifest_invoice?.TgTTTBSo)}
                    </Text>
                  ) : null}
                </IndexTable.Cell>
                <IndexTable.Cell />
                <IndexTable.Cell />
                <IndexTable.Cell>
                  <strong>{_orders?.total_seat}</strong>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text variant="bodyMd" fontWeight="bold" as="span">
                    {invoice_name}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text variant="bodyMd" fontWeight="bold" as="span">
                    {invoice_phone}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text variant="bodyMd" fontWeight="bold" as="span">
                    {invoice_email}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{_orders?.transactions?.payment_method}</IndexTable.Cell>
                <IndexTable.Cell>{totalPrice}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Badge size="small" tone={tax_type === 'COMPANY' ? 'critical' : 'info'}>
                    {tax_type}
                  </Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {isTicketExported && orderInvoice ? (
                    <Link
                      url={process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${orderInvoice?.trip_manifest_invoice?.maTraCuu}` || '#'}
                      target="_blank"
                    >
                      {orderInvoice?.trip_manifest_invoice?.shDon}
                    </Link>
                  ) : null}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {orderInvoice ? (
                    <Text as="span" alignment="end" numeric>
                      {helpers.formatNumberWithCommas(orderInvoice?.trip_manifest_invoice?.TgTTTBSo)}
                    </Text>
                  ) : null}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {orderInvoice ? (
                    <Text as="span" alignment="end" tone="subdued">
                      {isSigned ? 'Đã phát hành' : 'Chưa phát hành'}
                    </Text>
                  ) : null}
                  {!isTicketExported && (
                    <InlineStack align="end" gap="200">
                      <Button icon={EditIcon} size="micro" onClick={() => handleEditTax(_orders.id)}></Button>
                      {order_invoice_info ? (
                        <>
                          {Number(manifest?.total_occupied_seats || 0) === 1 ? (
                            <Button
                              icon={AppExtensionIcon}
                              size="micro"
                              loading={isPreviewLoading || isExportLoading}
                              onClick={() => getPreviewDataCallback(buildInvoicePayload('contract', manifest?.id, orderId), String(orderId))}
                            >
                              Xuất thuê bao
                            </Button>
                          ) : null}

                          <Button
                            icon={AppExtensionIcon}
                            size="micro"
                            loading={isPreviewLoading || isExportLoading}
                            onClick={() => getPreviewDataCallback(buildInvoicePayload('each_order', manifest?.id, orderId), String(orderId))}
                          >
                            Xuất vé
                          </Button>
                        </>
                      ) : null}
                    </InlineStack>
                  )}
                </IndexTable.Cell>
              </IndexTable.Row>
            );
          });
        })()}
      </Fragment>
    );
  });

  return (
    <>
      {isMobile ? (
        <div>
          <InlineStack align="end" blockAlign="center">
            <Button variant="plain" icon={FilterIcon} size="micro" onClick={() => setShowFilters((prev) => !prev)}>
              {showFilters ? 'Đóng bộ lọc' : 'Mở bộ lọc'}
            </Button>
          </InlineStack>
          <br />
        </div>
      ) : null}

      {!isMobile || showFilters ? (
        <Card padding="400">
          <InlineStack gap="400" wrap>
            <Box minWidth={isMobile ? '100%' : '260px'} width={isMobile ? '100%' : undefined}>
              <Text as="p" variant="bodySm" tone="subdued">
                Ngày khởi hành
              </Text>
              <Box minWidth={isMobile ? '100%' : '276px'} width={isMobile ? '100%' : undefined} padding={{ xs: '200' }}>
                <Popover
                  active={visible}
                  autofocusTarget="none"
                  preferredAlignment="left"
                  fullWidth
                  preferInputActivator={false}
                  preferredPosition="below"
                  preventCloseOnChildOverlayClick
                  onClose={() => setVisible(false)}
                  activator={
                    <TextField
                      role="combobox"
                      label={'Start date'}
                      labelHidden
                      prefix={<Icon source={CalendarIcon} />}
                      value={dateandtime.format(selectedDate, 'DD-MM-YYYY')}
                      onFocus={() => setVisible(true)}
                      // onChange={handleInputValueChange}
                      autoComplete="off"
                    />
                  }
                >
                  <Card>
                    <DatePicker month={month} year={year} selected={selectedDate} onMonthChange={handleMonthChange} onChange={handleDateSelection} />
                  </Card>
                </Popover>
              </Box>
            </Box>
            <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
              <Select
                label="Tuyến đường"
                options={journeyOptions}
                onChange={(value) =>
                  setMainQuery((prev) => ({
                    ...prev,
                    'trip.journey_id': value,
                    page: 1,
                  }))
                }
                value={mainQuery['trip.journey_id'] || ''}
              />
            </Box>
            <Box minWidth={isMobile ? '100%' : '200px'} width={isMobile ? '100%' : undefined}>
              <Select
                label="Loại hình vận chuyển"
                options={seatingOptions}
                onChange={(value) =>
                  setMainQuery((prev) => ({
                    ...prev,
                    seating_id: value,
                    page: 1,
                  }))
                }
                value={mainQuery.seating_id || ''}
              />
            </Box>
            <Box minWidth={isMobile ? '100%' : '200px'} width={isMobile ? '100%' : undefined}>
              <Select
                label="Biển số xe"
                options={licenseOptions}
                onChange={(value) =>
                  setMainQuery((prev) => ({
                    ...prev,
                    vehicle_id: value,
                    page: 1,
                  }))
                }
                value={mainQuery.vehicle_id || ''}
              />
            </Box>
            <Box minWidth={isMobile ? '100%' : '200px'} width={isMobile ? '100%' : undefined}>
              <Select
                label="T.t xuất hóa đơn"
                options={[
                  {
                    label: 'Tất cả',
                    value: '',
                  },
                  {
                    label: 'Chưa xuất',
                    value: '0',
                  },
                  {
                    label: 'Đã xuất',
                    value: '1',
                  },
                ]}
                onChange={(value) =>
                  setMainQuery((prev) => ({
                    ...prev,
                    is_exported_invoice: value,
                    page: 1,
                  }))
                }
                value={mainQuery.is_exported_invoice || ''}
              />
            </Box>
            <Box minWidth={isMobile ? '100%' : '200px'} width={isMobile ? '100%' : undefined}>
              <Select
                label="Loại hóa đơn"
                options={[
                  {
                    label: 'Tất cả',
                    value: '',
                  },
                  {
                    label: 'Công ty',
                    value: 'COMPANY',
                  },
                  {
                    label: 'Cá nhân',
                    value: 'PERSONAL',
                  },
                ]}
                onChange={(value) =>
                  setMainQuery((prev) => ({
                    ...prev,
                    'trip_manifest_invoice.TaxType': value,
                    page: 1,
                  }))
                }
                value={mainQuery['trip_manifest_invoice.TaxType'] || ''}
              />
            </Box>
          </InlineStack>
        </Card>
      ) : null}

      <Box background="bg">
        <div className="invoice_managers_wrapper">
          <IndexTable
            lastColumnSticky={false}
            selectable={false}
            resourceName={resourceName}
            itemCount={entities?.length || 0}
            hasZebraStriping
            emptyState={
              <Text as="p" tone="subdued">
                {loading ? 'Đang tải dữ liệu...' : 'Không có chuyến phù hợp với bộ lọc.'}
              </Text>
            }
            headings={columnHeadings}
          >
            {rowMarkup}
          </IndexTable>
        </div>
      </Box>

      {totalItems > 0 ? (
        <Box paddingBlockStart="200" paddingBlockEnd="200">
          <InlineStack align="end">
            <Text as="span" tone="subdued">
              Tổng: {helpers.formatNumberWithCommas(sum.countTrip)} chuyến • {helpers.formatNumberWithCommas(sum.countVehicle)} xe •{' '}
              {helpers.formatNumberWithCommas(sum.totalPassenger)} khách • {helpers.formatNumberWithCommas(sum.totalTicket)} vé •{' '}
              {helpers.formatNumberWithCommas(sum.totalMoney)}
            </Text>
          </InlineStack>
        </Box>
      ) : null}

      <br />
      {totalItems > 0 ? (
        <Pagination
          TotalRecord={totalItems}
          onChange={onChangePagination}
          pageSize={Number(mainQuery.limit)}
          activeCurrentPage={Number(mainQuery.page)}
        />
      ) : null}

      <br />
      <br />
      <br />
      <br />
      <br />

      {showEditTaxModal && currentOrderId ? (
        <EditOrderTaxModal
          open={showEditTaxModal}
          orderId={currentOrderId}
          onClose={(reload) => {
            setShowEditTaxModal(false);
            if (reload) {
              getEntities();
            }
          }}
        />
      ) : null}
      <AccountingInvoicePreviewModal
        open={previewModalOpen}
        data={previewData}
        loading={exportingInvoice}
        onClose={() => setPreviewModalOpen(false)}
        onCreateInvoice={() => {
          if (!previewRequest) return;
          handleExportInvoice(previewRequest);
        }}
      />
    </>
  );
}

